<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import User from '@/api/User'
import moment from 'moment'
import { extractTimeFromQuery, formatTimeRange } from '@/components/EventSearch/timeRangeUtils'

const emit = defineEmits(['execute-query'])

// State
const queries = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const error = ref('')
const searchInput = ref('')
const timeFilter = ref('all')
const offset = ref(0)
const limit = 50
const total = ref(0)
const hasMore = ref(false)
const loadMoreTrigger = ref(null)
const queriesList = ref(null)
let observer = null
let searchTimeout = null

// Compute start/end ISO strings from the selected time filter
const timeRange = computed(() => {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  switch (timeFilter.value) {
    case 'today':
      return { start: startOfToday.toISOString(), end: '' }
    case 'yesterday': {
      const startOfYesterday = new Date(startOfToday)
      startOfYesterday.setDate(startOfYesterday.getDate() - 1)
      return { start: startOfYesterday.toISOString(), end: startOfToday.toISOString() }
    }
    case 'week': {
      const dayOfWeek = startOfToday.getDay()
      const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const startOfThisWeek = new Date(startOfToday)
      startOfThisWeek.setDate(startOfThisWeek.getDate() - daysSinceMonday)
      const startOfLastWeek = new Date(startOfThisWeek)
      startOfLastWeek.setDate(startOfLastWeek.getDate() - 7)
      return { start: startOfLastWeek.toISOString(), end: startOfThisWeek.toISOString() }
    }
    case 'older': {
      const dayOfWeek = startOfToday.getDay()
      const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const startOfThisWeek = new Date(startOfToday)
      startOfThisWeek.setDate(startOfThisWeek.getDate() - daysSinceMonday)
      const startOfLastWeek = new Date(startOfThisWeek)
      startOfLastWeek.setDate(startOfLastWeek.getDate() - 7)
      return { start: '', end: startOfLastWeek.toISOString() }
    }
    default:
      return { start: '', end: '' }
  }
})

async function fetchQueries(isLoadMore = false) {
  if (!hasMore.value && isLoadMore) return
  if (loading.value || loadingMore.value) return

  if (isLoadMore) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  error.value = ''

  try {
    const { start, end } = timeRange.value
    const response = await User.getQueries(
      offset.value,
      limit,
      searchInput.value,
      start,
      end,
      'event'
    )

    if (response.status === 'success') {
      const data = response.data || {}
      const items = data.queries || []

      if (isLoadMore) {
        queries.value = [...queries.value, ...items]
      } else {
        queries.value = items
      }

      total.value = data.total || 0
      hasMore.value = data.has_more || false
      offset.value += items.length
    } else {
      error.value = response.error || 'Failed to load query history'
      hasMore.value = false
    }
  } catch (err) {
    console.error('Error fetching query history:', err)
    error.value = err.response?.data?.message || 'An unexpected error occurred'
    hasMore.value = false
  } finally {
    loading.value = false
    loadingMore.value = false

    // Re-setup observer after non-loadMore fetches since the queries-list
    // element gets unmounted/remounted by v-else conditional rendering
    if (!isLoadMore) {
      await nextTick()
      setupIntersectionObserver()
    }
  }
}

function resetAndLoad() {
  offset.value = 0
  queries.value = []
  hasMore.value = true
  fetchQueries()
}

function handleSearchInput() {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => resetAndLoad(), 300)
}

function handleTimeFilterChange() {
  resetAndLoad()
}

function handleQueryClick(queryText) {
  emit('execute-query', queryText)
}

function setupIntersectionObserver() {
  if (!loadMoreTrigger.value) return

  if (observer) {
    observer.disconnect()
    observer = null
  }

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry.isIntersecting && hasMore.value && !loadingMore.value) {
        fetchQueries(true)
      }
    },
    {
      root: queriesList.value,
      rootMargin: '100px',
      threshold: 0.1
    }
  )

  observer.observe(loadMoreTrigger.value)
}

function formatTime(timestamp) {
  if (!timestamp) return ''
  return moment(timestamp).calendar()
}

function parseQuery(queryString) {
  const { cleanQuery, start, end } = extractTimeFromQuery(queryString)
  return {
    text: cleanQuery || queryString,
    timeLabel: formatTimeRange(start, end)
  }
}

onMounted(() => {
  fetchQueries()
  setTimeout(() => setupIntersectionObserver(), 100)
})

onBeforeUnmount(() => {
  if (observer && loadMoreTrigger.value) {
    observer.unobserve(loadMoreTrigger.value)
    observer.disconnect()
  }
  if (searchTimeout) clearTimeout(searchTimeout)
})
</script>

<template>
  <div class="query-history-view">
    <!-- Controls -->
    <div class="history-controls">
      <div class="controls-count">{{ total }} {{ total === 1 ? 'query' : 'queries' }}</div>
      <div class="search-row">
        <div class="search-field">
          <font-awesome-icon icon="magnifying-glass" class="search-icon" />
          <input
            v-model="searchInput"
            type="text"
            class="search-input"
            placeholder="Search past queries..."
            @input="handleSearchInput"
          />
        </div>
        <select v-model="timeFilter" class="time-filter" @change="handleTimeFilterChange">
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="week">Last Week</option>
          <option value="older">Older</option>
        </select>
      </div>
    </div>

    <!-- Loading State (initial) -->
    <div v-if="loading && queries.length === 0" class="state-container">
      <div class="spinner"></div>
      <p class="state-text">Loading query history...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="state-container">
      <font-awesome-icon icon="triangle-exclamation" class="error-icon" />
      <p class="state-text error-text">{{ error }}</p>
      <button class="retry-button" @click="resetAndLoad">Try Again</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="queries.length === 0 && !loading" class="state-container">
      <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="empty-icon" />
      <p class="state-text">{{ searchInput || timeFilter !== 'all' ? 'No queries match your filters' : 'No query history yet' }}</p>
      <p class="state-hint">{{ searchInput || timeFilter !== 'all' ? 'Try adjusting your search or time range' : 'Run an event search to start building history' }}</p>
    </div>

    <!-- Queries List -->
    <div v-else ref="queriesList" class="queries-list">
      <div class="queries-grid">
        <div
          v-for="(q, i) in queries"
          :key="q.id || i"
          class="query-item"
          @click="handleQueryClick(q.query)"
        >
          <div class="query-time">{{ formatTime(q.timestamp) }}</div>
          <div class="query-content">
            <code class="query-text">{{ parseQuery(q.query).text }}</code>
            <span class="time-badge">{{ parseQuery(q.query).timeLabel }}</span>
          </div>
        </div>
      </div>

      <!-- Intersection Observer Trigger -->
      <div ref="loadMoreTrigger" class="load-more-trigger"></div>

      <!-- Loading More -->
      <div v-if="loadingMore" class="loading-more">
        <div class="spinner-small"></div>
        <span>Loading more...</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.query-history-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #151a22;
  border-radius: 0.75rem;
  overflow: hidden;
}

/* Controls */
.history-controls {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.search-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.search-field {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 0.875rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.625rem 1rem 0.625rem 2.25rem;
  background: rgba(26, 29, 36, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  color: #f3f4f6;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.4);
  background: rgba(26, 29, 36, 0.8);
}

.search-input::placeholder {
  color: #6b7280;
}

.time-filter {
  padding: 0.625rem 1rem;
  background: rgba(26, 29, 36, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  color: #f3f4f6;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2.25rem;
}

.time-filter:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.4);
  background-color: rgba(26, 29, 36, 0.8);
}

.time-filter option {
  background: #1a1d24;
  color: #f3f4f6;
}

.controls-count {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
  min-height: 1.25rem;
  margin-bottom: 0.75rem;
}

/* State containers */
.state-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.state-text {
  color: #9ca3af;
  font-size: 0.875rem;
  margin: 0;
}

.error-text {
  color: #ef4444;
}

.state-hint {
  color: #6b7280;
  font-size: 0.8rem;
  margin: 0.5rem 0 0 0;
}

.error-icon {
  font-size: 2rem;
  color: #ef4444;
  opacity: 0.7;
  margin-bottom: 1rem;
}

.empty-icon {
  font-size: 2rem;
  color: #6b7280;
  opacity: 0.5;
  margin-bottom: 1rem;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1.25rem;
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.5rem;
  color: #7357D2;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: rgba(115, 87, 210, 0.25);
  border-color: rgba(115, 87, 210, 0.5);
}

/* Queries list */
.queries-list {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

.queries-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.query-item {
  background: rgba(26, 29, 36, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.query-item:hover {
  background: rgba(115, 87, 210, 0.08);
  border-color: rgba(115, 87, 210, 0.25);
}

.query-time {
  font-size: 0.7rem;
  color: #6b7280;
  transition: color 0.15s ease;
}

.query-item:hover .query-time {
  color: #9ca3af;
}

.query-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.query-text {
  color: #c4b5fd;
  font-size: 0.85rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.time-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  background: rgba(115, 87, 210, 0.15);
  border-radius: 0.375rem;
  font-size: 0.675rem;
  color: #a78bfa;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Load more */
.load-more-trigger {
  height: 20px;
  margin: 20px 0;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  color: #9ca3af;
  font-size: 0.8rem;
}

/* Spinners */
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #374151;
  border-top: 3px solid #7357D2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid #374151;
  border-top: 2px solid #7357D2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Scrollbar */
.queries-list::-webkit-scrollbar {
  width: 6px;
}

.queries-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.queries-list::-webkit-scrollbar-thumb {
  background: rgba(115, 87, 210, 0.25);
  border-radius: 3px;
}

.queries-list::-webkit-scrollbar-thumb:hover {
  background: rgba(115, 87, 210, 0.4);
}
</style>
